/**
 * Every tunable number the netcode contract leaves to the implementation, in
 * one place, with the reasoning attached.
 *
 * `docs/DESIGN.md` fixes the *shape* of the contract — "a board hash every N
 * pieces", "inputs are rate-limited per client", "garbage lands after a short
 * delay" — and deliberately does not fix N, the limit or the delay. This module
 * picks them. `docs/decisions/0014-netcode-cadences-and-limits.md` is the long
 * form; the comments here are the short form, kept next to the values so a
 * future change cannot happen without reading the reasoning.
 *
 * They live in `packages/protocol` rather than in `apps/server` because both
 * ends need them: the client has to send hashes on the cadence the server
 * expects, stay under the rate limit the server enforces, and schedule garbage
 * on the ticks the server names.
 */
import type { CurseEffect } from '@interlock/sim';
import type { MatchType } from './lobby.js';

/**
 * **N — the board-hash cadence.** A client sends `hashState` every time its
 * `piecesPlaced` count crosses a multiple of this.
 *
 * Four pieces is between two and four seconds of real play, so a client that
 * starts lying is caught within a few seconds rather than at the end of a
 * round — while costing well under one message per second in the steady state.
 * A cadence in *pieces* rather than frames is what makes the comparison
 * clock-free: the server compares its own hash at piece 4 against the client's
 * hash at piece 4, whenever each side happens to get there.
 */
export const HASH_EVERY_N_PIECES = 4;

/**
 * How many `(pieceCount, hash)` pairs either side keeps while waiting for its
 * counterpart. At the cadence above this is roughly a minute and a half of
 * play, which is far longer than any tolerable lag; the cap exists so a client
 * that never sends a hash cannot grow the server's memory.
 */
export const HASH_LEDGER_CAPACITY = 24;

/**
 * Mismatches tolerated before the client is disconnected.
 *
 * `docs/DESIGN.md`: "A hash mismatch resyncs the client from a server
 * snapshot. Repeated mismatches disconnect and log." One mismatch is a
 * dropped packet or a bug and gets a snapshot; three in a row is a client that
 * is not running the simulation it claims to be running. The count is per
 * round, and a successful comparison does *not* clear it — a client that
 * alternates honest and dishonest hashes is exactly the case the strike count
 * is for.
 */
export const HASH_MISMATCH_STRIKE_LIMIT = 3;

/**
 * How long a resync may go unacknowledged before it counts as another
 * mismatch.
 *
 * A resync bumps the seat's epoch, and the server ignores messages stamped
 * with the old one until the client adopts the new one — which is what stops
 * inputs that were already in flight from being replayed against a board that
 * no longer exists. A client that never adopts it would otherwise simply go
 * quiet: its simulation would stop advancing, its round would never end, and
 * it would never reach the strike limit either. So an unacknowledged repair is
 * a failed repair, and it costs a strike exactly like a bad hash does.
 *
 * Two seconds is several round trips plus the client's own flush interval —
 * long enough that a slow link is never mistaken for a client ignoring the
 * repair.
 */
export const RESYNC_ACK_TIMEOUT_MS = 2000;

/**
 * How far ahead of the server's own clock a client's reported frame may be
 * before the server stops replaying it. Twelve ticks is 200ms.
 *
 * The server replays a player's inputs up to `min(reportedFrame, elapsed +
 * this)`. It exists so a client cannot fast-forward its own simulation and
 * dump a burst of attacks the opponent never had time to react to: whatever a
 * tampered client claims, the authoritative replay never runs more than 200ms
 * ahead of real time. 200ms is generous for ordinary clock skew and jitter and
 * short enough that a burst inside the window is worth nothing.
 */
export const INPUT_LEAD_TICKS = 12;

/**
 * How far ahead of a victim's *reported* position the server schedules a
 * garbage batch to enter their queue. Thirty ticks is 500ms.
 *
 * Both the server's authoritative simulation and the victim's own copy must
 * insert the batch on the *same* tick, or their board hashes diverge at the
 * next comparison. The server therefore names an absolute tick in the future
 * rather than saying "now"; this lead is the budget for the message to get
 * there, and covers roughly 450ms of one-way latency on top of the input
 * flush interval below.
 */
export const GARBAGE_LEAD_TICKS = 30;

/**
 * The cancellation window: ticks between a batch entering the victim's queue
 * and the rows arriving. Sixty ticks is one second.
 *
 * `docs/DESIGN.md`: "Garbage lands after a short delay so the target can
 * cancel with their own clear." One second is about two hard drops for a fast
 * player — enough that a counter-attack is a real decision, short enough that
 * the pressure is felt. `docs/STATE.md` open question 9 asked phase 04 to pick
 * this; this is the pick.
 */
export const GARBAGE_DELAY_TICKS = 60;

/**
 * How often a client flushes its input batch to the server, in ticks. Four
 * ticks is about 15 messages per second.
 *
 * Each message carries every event since the last flush plus the frame the
 * client has reached, so nothing is lost by batching — only delayed by at most
 * 66ms, which is inside the garbage lead above.
 */
export const INPUT_FLUSH_TICKS = 4;

/**
 * How often the server broadcasts its authoritative per-seat readout — the
 * numbers the incoming-attack meter and the round score are drawn from.
 *
 * `docs/DESIGN.md`, duel: "Incoming meter fed by server state, not local
 * guessing." 100ms matches `docs/KNOWN-BUGS.md` item 5's 10Hz HUD throttle, so
 * the client never receives a number faster than it is willing to paint one.
 */
export const STATE_BROADCAST_MS = 100;

/**
 * How often the server flushes its relay of a seat's validated inputs to the
 * other clients, in server ticks. Same cadence as the input flush: the relay
 * is a re-broadcast of what arrived, and buffering it longer would only make
 * the opponent's board lag further behind for no saving.
 */
export const RELAY_FLUSH_TICKS = 4;

/**
 * **Rate limits, per client.**
 *
 * The ceiling is not a guess about how fast a person can move their hands; it
 * is what the *client* can structurally emit. `apps/client`'s input controller
 * turns held keys into at most one auto-repeat action per direction per tick
 * and one soft-drop per tick, on top of taps — call it five discrete actions
 * in a single 1/60s tick, and 300 a second. A real player with the default
 * 28ms ARR peaks nearer 75 a second, so this is four times a human and exactly
 * one times the machine.
 *
 * The burst allowance is two seconds' worth, so a client that stalls and then
 * flushes a backlog — a garbage-collection pause, a backgrounded tab — is not
 * punished for catching up.
 */
export const INPUT_EVENTS_PER_SECOND = 300;
export const INPUT_EVENT_BURST = 600;

/**
 * Messages per second, counted across every type a client may send. The client
 * sends roughly 15 input batches a second plus the odd hash and lobby action;
 * 40 leaves headroom of more than double without letting a client spin.
 */
export const MESSAGES_PER_SECOND = 40;
export const MESSAGE_BURST = 80;

/** Events accepted in one input batch. At the flush cadence above, four ticks of the ceiling. */
export const MAX_EVENTS_PER_INPUT_MESSAGE = 32;

/**
 * Rate-limit violations tolerated before the client is disconnected. A
 * violation drops the offending message; ten of them is a client that is not
 * going to stop.
 */
export const RATE_LIMIT_STRIKE_LIMIT = 10;

/** Rounds needed to win a match. `docs/DESIGN.md`: "best-of-three". */
export const ROUNDS_TO_WIN = 2;
export const BEST_OF = 3;

/** Milliseconds of countdown between "both players ready" and the first tick of a round. */
export const COUNTDOWN_MS = 3000;

/** Milliseconds a finished round is held on screen before the next one counts down. */
export const INTERMISSION_MS = 3000;

/** Spectator slots per room, on top of the two players. */
export const MAX_SPECTATORS = 8;

/* ------------------------------------------------------------------ *
 * Connection resilience (phase 05)
 *
 * `docs/decisions/0016`–`0019` are the long form of everything below.
 * ------------------------------------------------------------------ */

/**
 * **The grace window**, in milliseconds, by match type.
 *
 * `docs/DESIGN.md`, "Netcode contract": "the seat is held for 30 seconds in a
 * friendly match, 15 in ranked." Not a number this phase chose — a number this
 * phase implements. `graceWindowMs` is the one place either end reads it, so a
 * client's countdown and the server's deadline are the same arithmetic.
 */
export const GRACE_FRIENDLY_MS = 30_000;
export const GRACE_RANKED_MS = 15_000;

/** The grace window for a match type. The one reading of the spec's two numbers. */
export function graceWindowMs(matchType: MatchType): number {
  return matchType === 'ranked' ? GRACE_RANKED_MS : GRACE_FRIENDLY_MS;
}

/**
 * How long a **frozen** friendly match stays resumable before the room gives
 * up and the seats are released. Ten minutes.
 *
 * `docs/DESIGN.md`: "On grace expiry: friendly freezes and stays resumable."
 * "Resumable" cannot mean "forever" — a held seat is a reservation the
 * transport will not collect, so a room nobody returns to would live as long as
 * the process. Ten minutes is far longer than anyone waits for an opponent to
 * find a signal, and short enough that an abandoned room is not a leak.
 */
export const FROZEN_MATCH_TTL_MS = 600_000;

/**
 * **The resume countdown.** `docs/DESIGN.md`'s owner amendment, 2026-08-24:
 * "the server announces a resume tick and both clients show a 3-second
 * countdown, then play resumes together."
 *
 * Three seconds is `COUNTDOWN_MS` — deliberately the same length as the one
 * before a round's first tick, because it is the same promise: nobody's hands
 * are on the controls yet, and everybody sees the same three numbers. It is a
 * separate constant rather than a re-export because the two are the same
 * *today* by agreement, not by construction: a round countdown is about
 * getting ready, a resume countdown is about getting back, and either could
 * be retuned without the other.
 *
 * It is measured in wall-clock milliseconds and excluded from the played time
 * the authoritative ceiling is drawn against — a countdown nobody plays
 * through must not buy anybody a tick, for exactly the reason a freeze must
 * not (`docs/decisions/0019`).
 */
export const RESUME_COUNTDOWN_MS = 3000;

/**
 * **The consented-pause budget**: wall-clock milliseconds one seat may spend
 * *announced as paused* in a single round before the server stops treating it
 * as a pause.
 *
 * The whole match stops for a paused seat, which is what the amendment asks
 * for and is also, unbounded, a weapon: a player losing a round could hold the
 * other one hostage by toggling `visibilitychange` for ever. An unconsented
 * absence is already bounded — the grace window is exactly that bound — and
 * this is the same bound for the consented kind, per player, per round.
 *
 * Sixty seconds: twice the friendly grace window, which is long enough for a
 * real interruption (a phone call, a doorbell, switching apps to paste the
 * room code) and short enough that it cannot be a strategy. It is per round
 * rather than per match so a best-of-three does not silently triple it for
 * whoever hoarded it, and it is shown on both screens — a budget nobody can
 * see is a rule that feels like a bug when it fires.
 *
 * When it runs out the seat is treated as *absent* rather than forced back
 * online: the grace window starts, and the ordinary expiry outcomes follow
 * (friendly freezes and stays resumable, ranked forfeits). So the longest one
 * player can stop a friendly match without ever losing their socket is
 * `PAUSE_BUDGET_MS + GRACE_FRIENDLY_MS`, after which it is held rather than
 * running.
 */
export const PAUSE_BUDGET_MS = 60_000;

/**
 * **The heartbeat.** The client sends `ping` on this interval and the server
 * answers `pong`.
 *
 * It exists for one failure mode the transport cannot see: a WiFi-to-mobile
 * handoff drops the socket *silently*. No close frame arrives, no error fires,
 * and both ends believe they are connected — the client keeps sending into a
 * socket that goes nowhere and the server keeps waiting for a player who is
 * already gone. A reply that stops arriving is the only evidence there is.
 */
export const HEARTBEAT_INTERVAL_MS = 4000;

/**
 * How long the client waits for a `pong` before declaring the socket dead and
 * starting to reconnect. Ten seconds: two missed replies plus most of a third
 * interval, so a single dropped packet or a garbage-collection pause is never
 * mistaken for a dead link.
 *
 * This is the documented detection time for a silent death, and it is inside
 * every grace window — a handoff detected at 10s still leaves 20 seconds of a
 * friendly window (5 of a ranked one) to get back.
 */
export const HEARTBEAT_TIMEOUT_MS = 10_000;

/**
 * **The reconnect ladder.** `docs/prompts/05-resilience.md`: "Heartbeat, and
 * exponential backoff reconnect: 1s, 2s, 4s, 8s, capped."
 *
 * The last entry is the cap: attempts past the fourth keep waiting 8 seconds
 * until the grace window is exhausted. Doubling rather than retrying tightly
 * because the common cause of a failed attempt is a network that is still
 * changing underneath — a handoff mid-flight, a captive portal, a radio
 * reassociating — and hammering it neither helps nor is free on a phone's
 * battery.
 */
export const RECONNECT_BACKOFF_MS: readonly number[] = [1000, 2000, 4000, 8000];

/**
 * Delay before reconnect attempt number `attempt` (0-based), capped at the last
 * rung. One function so the client's ladder and its tests read the same table.
 */
export function reconnectDelayMs(attempt: number): number {
  const index = Math.min(Math.max(0, Math.trunc(attempt)), RECONNECT_BACKOFF_MS.length - 1);
  return RECONNECT_BACKOFF_MS[index] ?? RECONNECT_BACKOFF_MS[0] ?? 1000;
}

/**
 * How much of its own input history a client keeps, in ticks. 2400 is 40
 * seconds.
 *
 * Phase 05 kept this so a rejoining duel client could re-send the play the
 * server never saw. **Owner overhaul part 9 retired that**: an online match
 * now stops for everyone the moment a seat goes away, so there is no offline
 * play to hand over and a returner takes the server's snapshot flat
 * (`docs/decisions/0041`). What still reads the buffer is co-op's prediction,
 * which replays this client's own un-echoed inputs on top of every confirmed
 * relay — a handful of ticks in the ordinary case, and this is the bound on
 * how far it can grow when relays stop arriving.
 */
export const INPUT_HISTORY_TICKS = 2400;

/**
 * How often the server re-broadcasts the room view while a grace window is
 * counting down, so the other player's screen shows a live countdown rather
 * than a stale number. 250ms: four updates a second is smooth to read and is
 * well inside `docs/KNOWN-BUGS.md` item 5's 10Hz HUD budget.
 */
export const PRESENCE_BROADCAST_MS = 250;

/**
 * How often a match in progress is checkpointed to disk in the steady state.
 * Five seconds.
 *
 * Every *transition* checkpoints immediately — a round starting or ending, a
 * seat dropping, a rejoin, a freeze — so the periodic write only bounds how
 * much ordinary play a crash can cost. Five seconds of a duel is a few pieces,
 * and the cost is one small JSON file per live room per five seconds.
 */
export const CHECKPOINT_INTERVAL_MS = 5000;

/* ------------------------------------------------------------------ *
 * Curses (phase 07)
 *
 * `docs/decisions/0032` is the long form; these are the numbers, kept next to
 * everything else both ends must agree on for the same reason `limits.ts`'s
 * header gives. RUSH's gravity factor is not one of them: it changes hashed
 * state, so it is `@interlock/sim`'s `CURSE_RUSH_GRAVITY_FACTOR`.
 * ------------------------------------------------------------------ */

/** A loadout is exactly this many of `CURSE_EFFECTS`' four. */
export const CURSE_LOADOUT_SIZE = 3;

/**
 * How long each effect lasts once issued, in milliseconds.
 *
 * FOG and BLIND (presentation only) get the longer duration: they cost a
 * player information, not control, so a longer window is uncomfortable
 * rather than unfair. MIRROR and RUSH touch the simulation directly — an
 * inverted control scheme or doubled gravity is disorienting in a way that
 * stops being a curse and starts being a coin flip if it runs too long, so
 * both are shorter.
 */
export const CURSE_EFFECT_DURATION_MS: Readonly<Record<CurseEffect, number>> = {
  fog: 8000,
  blind: 8000,
  mirror: 6000,
  rush: 6000,
};

/**
 * How long, after one curse against a seat ends, before the next may start.
 *
 * `docs/prompts/07-specials.md`: "cooldowns so effects don't stack
 * unreadably." Five seconds is long enough that a player who just got FOGged
 * has time to see their stack again and react to whatever landed while it was
 * hidden, before the next one can begin — and short enough that a player who
 * keeps clearing doubles is still under real pressure.
 */
export const CURSE_COOLDOWN_MS = 5000;

/**
 * The clear that charges a curse. `docs/prompts/07-specials.md`'s own
 * example — "clearing a double or better" — is exactly what a clear's own
 * `attackSent` already measures: the attack table pays nothing for a single
 * outside a combo, so `attackSent > 0` *is* "a double or better, or a combo
 * that would have sent something". One number, already computed by the
 * engine for every other variant, rather than a second rule that has to stay
 * in step with the attack table.
 */
export const CURSE_TRIGGER_MIN_ATTACK = 1;

/* ------------------------------------------------------------------ *
 * Tug (phase 07)
 * ------------------------------------------------------------------ */

/**
 * The rope's scale, in charge units either side of centre. `docs/DESIGN.md`:
 * "define the scale, e.g. ±20 charge units." Twenty is roughly the attack a
 * good player sends in a minute of ordinary play (a double is 1, a quad or
 * T-spin double is 4, back-to-back and combo add more) — long enough that a
 * single lucky quad cannot end the match, short enough that a tug round
 * resolves in a comparable span to a duel round.
 */
export const TUG_ROPE_SCALE = 20;

/* ------------------------------------------------------------------ *
 * Handicap (phase 07)
 *
 * `docs/prompts/07-specials.md`: "the server tunes gravity and incoming
 * attack from past results, so mismatched friends still get close games...
 * keep multipliers modest and displayed." Two independent sources feed the
 * same shape of adjustment — see `duel-match.ts#seatHandicap`.
 * ------------------------------------------------------------------ */

/** Recorded matches an account needs before its win/loss record is trusted. */
export const HANDICAP_MIN_RECORDED_MATCHES = 5;

/** A win-rate gap this wide or wider gets the maximum ease below. */
export const HANDICAP_WINRATE_GAP_SPAN = 0.5;

/**
 * The most a signed-in mismatch may slow the weaker seat's gravity, in
 * thousandths. Modest: 150 is 15%. An integer because the ease it produces is
 * a gravity modifier, and those go into hashed state.
 */
export const HANDICAP_MAX_GRAVITY_EASE_PERMILLE = 150;

/** The most a signed-in mismatch may reduce incoming attack aimed at the weaker seat. 20%. */
export const HANDICAP_MAX_ATTACK_EASE = 0.2;

/** A flat, smaller ease for a guest seat down a round in the current match: 80 thousandths, 8% gravity. */
export const HANDICAP_GUEST_GRAVITY_EASE_PERMILLE = 80;

/** The guest fallback's flat attack ease. 12%. */
export const HANDICAP_GUEST_ATTACK_EASE = 0.12;

/**
 * WebSocket close codes this protocol uses. 4000–4999 is the range the
 * WebSocket standard reserves for applications.
 */
export const CLOSE_PROTOCOL_MISMATCH = 4010;
export const CLOSE_HASH_MISMATCH = 4011;
export const CLOSE_RATE_LIMIT = 4012;
export const CLOSE_ROOM_FULL = 4013;
/** The grace window ran out and the seat was not held any longer. */
export const CLOSE_GRACE_EXPIRED = 4014;
